/**
 * Load the whole episode before any of it is on screen.
 *
 * Phaser preloads what `pack.assets` declares, but that is only the stage: the
 * dialogue frame, the speaker plate, the choice plate and the rest are CSS
 * background images, and a CSS background is not fetched until the first frame
 * that paints it. So the box arrived a beat after the line it belonged to, a
 * character popped in mid-sentence, and the choice plates appeared blank and
 * then filled — every one of them the first time that art was needed.
 *
 * This fetches and DECODES every file the episode can reach before the title
 * card is shown. Decoding matters: a fetched-but-undecoded image still costs a
 * frame the moment it is first painted, which is exactly the hitch this exists
 * to remove.
 *
 * There is no board for this screen. It is deliberately plain — the episode's
 * own face, a rule, and a bar — rather than an invention dressed up as design.
 */
import { useEffect, useRef, useState } from 'react';
import type { PackManifest } from '@hon/schema';

/**
 * Art the DOM paints, which `pack.assets` does not cover.
 *
 * `pack.assets` is the stage's list — backgrounds and portraits, enforced by
 * lint rule 14. These are the pieces the React layer draws in CSS, and nothing
 * else knows they exist, so they are named here and nowhere else.
 */
const UI_ART = [
  'ui/dialogue-box.webp',
  'ui/dialogue-box-top.webp',
  'ui/speaker-plate.webp',
  'ui/choice-plate.webp',
  'ui/prompt-madiran.webp',
  'ui/chibi.webp',
  'ui/stat-panel.webp',
  'ui/end-button.webp',
  'ui/stat-icons.webp',
];

/** Fetch and decode one image. A failure must not strand the loader. */
async function warm(url: string): Promise<void> {
  try {
    const img = new Image();
    img.src = url;
    /* `decode()` waits for the pixels, not just the bytes. */
    await img.decode();
  } catch {
    /*
     * A missing or undecodable file is a content bug, not a reason to refuse
     * to start: the stage already skips art it has no texture for, and lint
     * rule 14 is what catches this properly. Log it and carry on.
     */
    console.warn(`[hon] could not preload ${url}`);
  }
}

export function Loading({
  pack,
  baseUrl,
  onDone,
}: {
  pack: PackManifest;
  baseUrl: string;
  onDone: () => void;
}) {
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(1);
  /* Strict mode mounts twice in dev; the episode must not load twice. */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const urls = [
      ...pack.assets.filter((a) => a.kind === 'image').map((a) => a.path),
      ...UI_ART,
    ].map((p) => `${baseUrl}/${p}`);

    setTotal(urls.length);
    let finished = 0;

    /*
     * No cleanup flag here on purpose. Strict mode mounts, cleans up and mounts
     * again; a flag set by the FIRST cleanup silences the SECOND run, which the
     * `started` ref has already made the only one that does any work — so every
     * update was discarded and the bar sat at 0% while the files loaded fine
     * behind it. A stray setState after unmount is harmless; a loader that
     * never finishes is not.
     */
    void Promise.all(urls.map((u) => warm(u).then(() => {
      finished++;
      setDone(finished);
    }))).then(onDone);
  }, [pack, baseUrl, onDone]);

  const pct = Math.round((done / total) * 100);

  return (
    <div className="loading" role="status" aria-live="polite">
      <p className="loading-name">{pack.books[0]?.title ?? ''}</p>
      <div className="loading-rule" aria-hidden="true" />
      <div
        className="loading-bar"
        role="progressbar"
        aria-label="Загрузка"
        aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
      >
        <i style={{ width: `${pct}%` }} />
      </div>
      <p className="loading-pct">{pct}%</p>
    </div>
  );
}
